/**
 * results contracts — owned by work package api-results-billing-admin.
 * Request/response schemas of the results, players, anomalies, billing and platform modules.
 * Names are prefixed by area (Results*, Players*, Anomalies*, Billing*, Platform*).
 */
import { z } from 'zod';
import {
  AnomalyKind,
  AnomalySeverity,
  AnomalyStatus,
  AwardType,
  ChargeStatus,
  Gender,
  PaymentStatus,
  PlayStatus,
  PricingMode,
  StatementStatus,
  TenantKind,
  TenantRole,
  TenantStatus,
  TournamentKind,
  TournamentStatus,
  UserStatus,
} from '../enums';
import { I18nText, LocaleSchema } from '../i18n';
import {
  ContestLeaderSchema,
  LeaderboardTiebreakSchema,
  LeaderboardViewSchema,
  MatchViewSchema,
} from './leaderboard';

/** Module-specific error codes (in addition to ErrorCode). Web maps them to `<ns>:errors.<CODE>`. */
export const ResultsErrorCode = {
  ROUNDS_NOT_LOCKED: 'ROUNDS_NOT_LOCKED',
  PLAYOFF_REQUIRED: 'PLAYOFF_REQUIRED',
  ALREADY_FINALIZED: 'ALREADY_FINALIZED',
  NOT_FINALIZED: 'NOT_FINALIZED',
  CHARGE_INVOICED: 'CHARGE_INVOICED',
  SNAPSHOT_NOT_FOUND: 'SNAPSHOT_NOT_FOUND',
  PROFILE_NOT_FOUND: 'PROFILE_NOT_FOUND',
  PROFILE_ALREADY_CLAIMED: 'PROFILE_ALREADY_CLAIMED',
  INVALID_PLAYER_TOKEN: 'INVALID_PLAYER_TOKEN',
  MERGE_SAME_PROFILE: 'MERGE_SAME_PROFILE',
  NOTHING_TO_BILL: 'NOTHING_TO_BILL',
  MIXED_CURRENCY: 'MIXED_CURRENCY',
  STATEMENT_NOT_PAYABLE: 'STATEMENT_NOT_PAYABLE',
  PROVIDER_NOT_FOUND: 'PROVIDER_NOT_FOUND',
  INVALID_TIERS: 'INVALID_TIERS',
  PLAN_NOT_USABLE: 'PLAN_NOT_USABLE',
  SLUG_TAKEN: 'SLUG_TAKEN',
  CANNOT_MODIFY_SELF: 'CANNOT_MODIFY_SELF',
  MATCHES_UNDECIDED: 'MATCHES_UNDECIDED',
  CLAIM_VERIFICATION_FAILED: 'CLAIM_VERIFICATION_FAILED',
  /** 409: a payment with this provider reference is already recorded (details.statementId, number) */
  PAYMENT_REF_TAKEN: 'PAYMENT_REF_TAKEN',
} as const;
export type ResultsErrorCode = (typeof ResultsErrorCode)[keyof typeof ResultsErrorCode];

/** Decimal money as a string ("7200000.00"). */
const Money = z.string();
const DateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const page = <T extends z.ZodType>(item: T) =>
  z.object({ items: z.array(item), nextCursor: z.string().nullable() });

// =============================================================================================
// results
// =============================================================================================

export const ResultsKind = z.enum(['LIVE', 'PROVISIONAL', 'FINAL']);
export type ResultsKind = z.infer<typeof ResultsKind>;

export const ResultsCategoryWinnerSchema = z.object({
  place: z.number(),
  competitorId: z.string(),
  name: z.string(),
  bib: z.number().nullable(),
  positionLabel: z.string(),
  primary: z.number().nullable(),
  gross: z.number().nullable(),
  net: z.number().nullable(),
  points: z.number().nullable(),
  toPar: z.number().nullable(),
});

/** 분류별 시상: one block per (awardRanking category, option). */
export const ResultsCategoryAwardSchema = z.object({
  categoryId: z.uuid(),
  categoryKey: z.string(),
  categoryLabel: I18nText,
  optionValue: z.string(),
  optionLabel: I18nText,
  view: z.string(),
  places: z.number(),
  excludeOverallTop: z.number(),
  allowDuplicateAwards: z.boolean(),
  winners: z.array(ResultsCategoryWinnerSchema),
});
export type ResultsCategoryAward = z.infer<typeof ResultsCategoryAwardSchema>;

/** 특별상 (hole-in-one, eagle...) with detected / confirmed winners. */
export const ResultsSpecialAwardSchema = z.object({
  awardId: z.uuid(),
  type: AwardType,
  name: z.string(),
  holeNumber: z.number().nullable(),
  roundId: z.uuid().nullable(),
  prize: z.string().nullable(),
  winners: z.array(
    z.object({
      winnerId: z.uuid(),
      participantId: z.uuid(),
      name: z.string(),
      bib: z.number().nullable(),
      /** 0 = not round specific */
      roundNo: z.number(),
      /** 0 = not hole specific */
      holeNumber: z.number(),
      strokes: z.number().nullable(),
      confirmed: z.boolean(),
    }),
  ),
});
export type ResultsSpecialAward = z.infer<typeof ResultsSpecialAwardSchema>;

/** ResultSnapshot.data (stored) */
export const ResultsSnapshotDataSchema = z.object({
  primaryViewKey: z.string().nullable(),
  views: z.record(z.string(), LeaderboardViewSchema),
  categoryAwards: z.array(ResultsCategoryAwardSchema),
  contests: z.array(ContestLeaderSchema),
  awards: z.array(ResultsSpecialAwardSchema),
  /** match play: every match (bracket order); [] for stroke formats and pre-existing snapshots */
  matches: z.array(MatchViewSchema).default([]),
});
export type ResultsSnapshotData = z.infer<typeof ResultsSnapshotDataSchema>;

export const ResultsQuerySchema = z.object({ kind: ResultsKind.optional() });

export const ResultsResponseSchema = ResultsSnapshotDataSchema.extend({
  tournamentId: z.uuid(),
  kind: ResultsKind,
  status: TournamentStatus,
  /** realtime version the results reflect */
  version: z.number(),
  generatedAt: z.iso.datetime(),
  finalizedAt: z.iso.datetime().nullable(),
  /** null for LIVE */
  snapshotId: z.uuid().nullable(),
});
export type ResultsResponse = z.infer<typeof ResultsResponseSchema>;

export const ResultsUnfinalizeRequestSchema = z.object({
  reason: z.string().trim().min(1).max(500),
});

export const ResultsUnfinalizeResponseSchema = z.object({
  tournamentId: z.uuid(),
  status: z.literal('COMPLETED'),
  version: z.number(),
  chargeStatus: ChargeStatus.nullable(),
});

/** Export query: ?lang&view&cat=key:value&kind&roundId */
export const ResultsExportQuerySchema = z.object({
  lang: LocaleSchema.optional(),
  view: z.string().max(40).optional(),
  cat: z.union([z.string(), z.array(z.string())]).optional(),
  kind: ResultsKind.optional(),
  roundId: z.uuid().optional(),
});

// =============================================================================================
// billing
// =============================================================================================

export const BillingTierSchema = z.object({
  minPlayers: z.int().min(1),
  /** null = no upper bound */
  maxPlayers: z.int().min(1).nullable(),
  unitPrice: Money,
});

export const BillingChargeLineSchema = z.object({
  minPlayers: z.number(),
  maxPlayers: z.number().nullable(),
  unitPrice: Money,
  players: z.number(),
  amount: Money,
});

/** TournamentCharge.breakdown (stored snapshot of the plan + calculation). */
export const BillingBreakdownSchema = z.object({
  planId: z.uuid().nullable(),
  planName: z.string(),
  mode: PricingMode,
  currency: z.string(),
  billablePlayers: z.number(),
  lines: z.array(BillingChargeLineSchema),
  flatFee: Money.nullable(),
  minimumFee: Money.nullable(),
  minimumApplied: z.boolean(),
  tiers: z.array(BillingTierSchema),
  amount: Money,
});
export type BillingBreakdown = z.infer<typeof BillingBreakdownSchema>;

export const BillingChargeSchema = z.object({
  id: z.uuid(),
  tournamentId: z.uuid(),
  tournamentName: z.string(),
  tournamentStatus: TournamentStatus,
  startDate: DateOnly,
  endDate: DateOnly,
  tenantId: z.uuid(),
  tenantName: z.string(),
  billToTenantId: z.uuid(),
  billToTenantName: z.string(),
  statementId: z.uuid().nullable(),
  status: ChargeStatus,
  billablePlayers: z.number(),
  currency: z.string(),
  amount: Money,
  breakdown: BillingBreakdownSchema,
  computedAt: z.iso.datetime(),
  finalizedAt: z.iso.datetime().nullable(),
  /**
   * live head counts behind billablePlayers (GET /tournaments/:tid/billing only; additive):
   * approved = APPROVED participants, billable = the live count with the §15 #15 rule (equals
   * billablePlayers while ESTIMATE), playedWdDq = billable players with WD/DQ, dns = APPROVED DNS,
   * caddies = active caddy accounts (never billed)
   */
  players: z
    .object({
      approved: z.number(),
      billable: z.number(),
      playedWdDq: z.number(),
      dns: z.number(),
      caddies: z.number(),
    })
    .optional(),
});
export type BillingCharge = z.infer<typeof BillingChargeSchema>;

/** POST /tournaments/:tid/finalize */
export const ResultsFinalizeResponseSchema = z.object({
  tournamentId: z.uuid(),
  status: z.literal('FINALIZED'),
  finalizedAt: z.iso.datetime(),
  snapshotId: z.uuid(),
  version: z.number(),
  participantResults: z.number(),
  charge: BillingChargeSchema,
});

export const BillingChangeBillToRequestSchema = z.object({
  billToTenantId: z.uuid(),
  reason: z.string().trim().min(1).max(500),
});

export const BillingPricingPlanSchema = z.object({
  id: z.uuid(),
  ownerTenantId: z.uuid().nullable(),
  name: z.string(),
  mode: PricingMode,
  currency: z.string(),
  flatFee: Money.nullable(),
  minimumFee: Money.nullable(),
  isDefault: z.boolean(),
  active: z.boolean(),
  tiers: z.array(BillingTierSchema),
  tenantCount: z.number(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type BillingPricingPlan = z.infer<typeof BillingPricingPlanSchema>;

export const BillingTenantPricingSchema = z.object({
  tenantId: z.uuid(),
  /** where the plan came from: Tenant.pricingPlanId / tenant-owned default / platform default */
  source: z.enum(['ASSIGNED', 'TENANT_DEFAULT', 'PLATFORM_DEFAULT']),
  plan: BillingPricingPlanSchema,
});

export const BillingChargesQuerySchema = z.object({
  from: DateOnly.optional(),
  to: DateOnly.optional(),
  status: ChargeStatus.optional(),
  role: z.enum(['billTo', 'owner']).default('billTo'),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export const BillingChargeListSchema = page(BillingChargeSchema);

export const BillingPaymentSchema = z.object({
  id: z.uuid(),
  provider: z.string(),
  providerRef: z.string().nullable(),
  amount: Money,
  currency: z.string(),
  status: PaymentStatus,
  note: z.string().nullable(),
  createdAt: z.iso.datetime(),
});

export const BillingStatementSummarySchema = z.object({
  id: z.uuid(),
  tenantId: z.uuid(),
  tenantName: z.string(),
  number: z.string(),
  periodStart: DateOnly,
  periodEnd: DateOnly,
  currency: z.string(),
  subtotal: Money,
  tax: Money,
  total: Money,
  paid: Money,
  status: StatementStatus,
  chargeCount: z.number(),
  issuedAt: z.iso.datetime().nullable(),
  dueAt: z.iso.datetime().nullable(),
  paidAt: z.iso.datetime().nullable(),
  note: z.string().nullable(),
  createdAt: z.iso.datetime(),
});
export type BillingStatementSummary = z.infer<typeof BillingStatementSummarySchema>;

export const BillingStatementDetailSchema = BillingStatementSummarySchema.extend({
  billing: z.object({
    billingName: z.string().nullable(),
    billingAddress: z.string().nullable(),
    taxCode: z.string().nullable(),
    contactEmail: z.string().nullable(),
  }),
  charges: z.array(BillingChargeSchema),
  payments: z.array(BillingPaymentSchema),
});
export type BillingStatementDetail = z.infer<typeof BillingStatementDetailSchema>;

export const BillingStatementListSchema = page(BillingStatementSummarySchema);

export const BillingCreateStatementRequestSchema = z
  .object({
    periodStart: DateOnly,
    periodEnd: DateOnly,
    /** tax rate as a fraction (0.1 = 10 %); default 0 (§15 #14) */
    taxRate: z.number().min(0).max(1).default(0),
    note: z.string().max(1000).optional(),
  })
  .refine((v) => v.periodStart <= v.periodEnd, {
    message: 'periodStart must be <= periodEnd',
    path: ['periodEnd'],
  });

export const BillingIssueStatementRequestSchema = z.object({
  /** default issuedAt + 30 days */
  dueAt: z.iso.datetime().optional(),
});
export const BillingVoidStatementRequestSchema = z.object({
  reason: z.string().trim().min(1).max(500),
});
export const BillingManualPaymentRequestSchema = z.object({
  amount: z
    .string()
    .regex(/^\d+(\.\d{1,2})?$/)
    .refine((v) => Number(v) > 0, 'Payment amount must be positive'),
  providerRef: z.string().trim().min(1).max(120),
  note: z.string().max(500).optional(),
});

export const BillingProviderSchema = z.object({
  key: z.string(),
  name: z.string(),
  supportsWebhook: z.boolean(),
  supportsRefund: z.boolean(),
});

// =============================================================================================
// platform (super admin)
// =============================================================================================

export const PlatformTenantSchema = z.object({
  id: z.uuid(),
  slug: z.string(),
  name: z.string(),
  kind: TenantKind,
  status: TenantStatus,
  country: z.string(),
  timezone: z.string(),
  defaultLocale: z.string(),
  pricingPlanId: z.uuid().nullable(),
  pricingPlanName: z.string().nullable(),
  tournamentCount: z.number(),
  lastCharge: z
    .object({
      tournamentId: z.uuid(),
      tournamentName: z.string(),
      status: ChargeStatus,
      amount: Money,
      currency: z.string(),
      computedAt: z.iso.datetime(),
    })
    .nullable(),
  createdAt: z.iso.datetime(),
});
export type PlatformTenant = z.infer<typeof PlatformTenantSchema>;

export const PlatformTenantDetailSchema = PlatformTenantSchema.extend({
  contactName: z.string().nullable(),
  contactEmail: z.string().nullable(),
  contactPhone: z.string().nullable(),
  billingName: z.string().nullable(),
  billingAddress: z.string().nullable(),
  taxCode: z.string().nullable(),
  clubId: z.uuid().nullable(),
  courseCount: z.number(),
  members: z.array(
    z.object({
      membershipId: z.uuid(),
      userId: z.uuid(),
      email: z.string().nullable(),
      displayName: z.string(),
      role: TenantRole,
      status: UserStatus,
    }),
  ),
});
export type PlatformTenantDetail = z.infer<typeof PlatformTenantDetailSchema>;

export const PlatformTenantsQuerySchema = z.object({
  kind: TenantKind.optional(),
  status: TenantStatus.optional(),
  q: z.string().max(100).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export const PlatformTenantListSchema = page(PlatformTenantSchema);

const TenantContactFields = {
  contactName: z.string().max(200).nullable().optional(),
  contactEmail: z.email().max(200).nullable().optional(),
  contactPhone: z.string().max(40).nullable().optional(),
  billingName: z.string().max(200).nullable().optional(),
  billingAddress: z.string().max(500).nullable().optional(),
  taxCode: z.string().max(40).nullable().optional(),
};

export const PlatformCreateTenantRequestSchema = z.object({
  name: z.string().trim().min(1).max(200),
  slug: z
    .string()
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
    .max(80)
    .optional(),
  /**
   * GOLF_COURSE / ORGANIZER / SOCIETY (golf club registered by the operator, §15 #441 — it gets
   * its Club row too, like a club made through POST /clubs). PERSONAL spaces are never created here.
   */
  kind: z.enum(['GOLF_COURSE', 'ORGANIZER', 'SOCIETY']),
  country: z.string().length(2).default('VN'),
  timezone: z.string().max(60).default('Asia/Ho_Chi_Minh'),
  defaultLocale: LocaleSchema.default('vi'),
  pricingPlanId: z.uuid().nullable().optional(),
  ...TenantContactFields,
  admin: z.object({
    email: z.email().max(200),
    displayName: z.string().trim().min(1).max(120).optional(),
    password: z.string().min(8).max(200).optional(),
  }),
});

export const PlatformAdminLinkResultSchema = z.object({
  userId: z.uuid(),
  membershipId: z.uuid(),
  role: TenantRole,
  /** true when a new user account was created */
  created: z.boolean(),
  /** shown ONCE when a new account was created without a password */
  temporaryPassword: z.string().nullable(),
});

export const PlatformCreateTenantResponseSchema = z.object({
  tenant: PlatformTenantDetailSchema,
  admin: PlatformAdminLinkResultSchema,
});

export const PlatformUpdateTenantRequestSchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  status: TenantStatus.optional(),
  pricingPlanId: z.uuid().nullable().optional(),
  timezone: z.string().max(60).optional(),
  defaultLocale: LocaleSchema.optional(),
  country: z.string().length(2).optional(),
  ...TenantContactFields,
});

export const PlatformAddAdminRequestSchema = z.object({
  email: z.email().max(200),
  displayName: z.string().trim().min(1).max(120).optional(),
  role: TenantRole.default('TENANT_ADMIN'),
  password: z.string().min(8).max(200).optional(),
});

export const PlatformTournamentsQuerySchema = z.object({
  status: TournamentStatus.optional(),
  tenantId: z.uuid().optional(),
  from: DateOnly.optional(),
  to: DateOnly.optional(),
  q: z.string().max(100).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export const PlatformTournamentSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  kind: TournamentKind,
  status: TournamentStatus,
  tenantId: z.uuid(),
  tenantName: z.string(),
  startDate: DateOnly,
  endDate: DateOnly,
  participants: z.number(),
  /** live SSE connections across all api instances (rt:instances) */
  connections: z.object({ public: z.number(), admin: z.number(), caddy: z.number() }),
  /** caddy sessions seen in the last 15 minutes */
  activeCaddies: z.number(),
  charge: z
    .object({
      status: ChargeStatus,
      amount: Money,
      currency: z.string(),
      billablePlayers: z.number(),
    })
    .nullable(),
});
export const PlatformTournamentListSchema = page(PlatformTournamentSchema);

export const PlatformMonitoringSchema = z.object({
  generatedAt: z.iso.datetime(),
  instances: z.array(
    z.object({
      instanceId: z.string(),
      total: z.number(),
      reportedAt: z.string().nullable(),
      stale: z.boolean(),
      byAudience: z.object({ public: z.number(), admin: z.number(), caddy: z.number() }),
    }),
  ),
  connections: z.object({
    total: z.number(),
    public: z.number(),
    admin: z.number(),
    caddy: z.number(),
  }),
  db: z.object({ status: z.enum(['ok', 'down']), latencyMs: z.number().nullable() }),
  redis: z.object({ status: z.enum(['ok', 'down']), latencyMs: z.number().nullable() }),
  ops: z.object({
    last5m: z.number(),
    last15m: z.number(),
    last60m: z.number(),
    /** ops per minute, oldest first (last 15 minutes) */
    perMinute: z.array(z.object({ minute: z.iso.datetime(), count: z.number() })),
  }),
  errors5xx: z.object({ last5m: z.number(), last60m: z.number() }),
  anomalies: z.object({ open: z.number(), critical: z.number() }),
  tournamentsInProgress: z.number(),
});
export type PlatformMonitoring = z.infer<typeof PlatformMonitoringSchema>;

export const PlatformUsersQuerySchema = z.object({
  q: z.string().max(100).optional(),
  status: UserStatus.optional(),
  superAdmin: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export const PlatformUserSchema = z.object({
  id: z.uuid(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  displayName: z.string(),
  locale: z.string(),
  status: UserStatus,
  isSuperAdmin: z.boolean(),
  lastLoginAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  tenants: z.array(z.object({ tenantId: z.uuid(), name: z.string(), role: TenantRole })),
});
export const PlatformUserListSchema = page(PlatformUserSchema);

export const PlatformUpdateUserRequestSchema = z.object({
  status: z.enum(['ACTIVE', 'DISABLED']).optional(),
  isSuperAdmin: z.boolean().optional(),
  displayName: z.string().trim().min(1).max(120).optional(),
});

export const PlatformResetPasswordResponseSchema = z.object({
  userId: z.uuid(),
  /** shown ONCE; every session of the user was revoked */
  temporaryPassword: z.string(),
});

export const PlatformCreatePlanRequestSchema = z.object({
  name: z.string().trim().min(1).max(120),
  mode: PricingMode,
  currency: z.string().length(3).default('VND'),
  tiers: z.array(BillingTierSchema).max(50).default([]),
  flatFee: Money.regex(/^\d+(\.\d{1,2})?$/)
    .nullable()
    .optional(),
  minimumFee: Money.regex(/^\d+(\.\d{1,2})?$/)
    .nullable()
    .optional(),
  ownerTenantId: z.uuid().nullable().optional(),
  isDefault: z.boolean().default(false),
});
export const PlatformUpdatePlanRequestSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  mode: PricingMode.optional(),
  currency: z.string().length(3).optional(),
  tiers: z.array(BillingTierSchema).max(50).optional(),
  flatFee: Money.regex(/^\d+(\.\d{1,2})?$/)
    .nullable()
    .optional(),
  minimumFee: Money.regex(/^\d+(\.\d{1,2})?$/)
    .nullable()
    .optional(),
  isDefault: z.boolean().optional(),
  active: z.boolean().optional(),
});
export const PlatformPlanListSchema = z.object({ items: z.array(BillingPricingPlanSchema) });

export const PlatformBillingSummaryQuerySchema = z.object({
  from: DateOnly.optional(),
  to: DateOnly.optional(),
  groupBy: z.enum(['billTo', 'owner', 'venue']).default('billTo'),
  status: ChargeStatus.optional(),
});

export const PlatformBillingSummarySchema = z.object({
  from: DateOnly.nullable(),
  to: DateOnly.nullable(),
  groupBy: z.enum(['billTo', 'owner', 'venue']),
  rows: z.array(
    z.object({
      groupTenantId: z.uuid().nullable(),
      groupTenantName: z.string(),
      currency: z.string(),
      tournaments: z.number(),
      billablePlayers: z.number(),
      amount: Money,
      byStatus: z.record(z.string(), Money),
    }),
  ),
  charges: z.array(
    z.object({
      chargeId: z.uuid(),
      tournamentId: z.uuid(),
      tournamentName: z.string(),
      startDate: DateOnly,
      tenantId: z.uuid(),
      tenantName: z.string(),
      billToTenantId: z.uuid(),
      billToTenantName: z.string(),
      /** owner tenant of the tournament's (first round) primary course */
      venueTenantId: z.uuid().nullable(),
      venueTenantName: z.string().nullable(),
      status: ChargeStatus,
      billablePlayers: z.number(),
      amount: Money,
      currency: z.string(),
    }),
  ),
  totals: z.array(z.object({ currency: z.string(), amount: Money, billablePlayers: z.number() })),
});
export type PlatformBillingSummary = z.infer<typeof PlatformBillingSummarySchema>;

// =============================================================================================
// players
// =============================================================================================

export const PlayersProfileSchema = z.object({
  id: z.uuid(),
  fullName: z.string(),
  gender: Gender.nullable(),
  birthYear: z.number().nullable(),
  nationality: z.string().nullable(),
  isPro: z.boolean(),
  /** "PinGo HI (unofficial)" */
  handicapIndex: z.number().nullable(),
  handicapUpdatedAt: z.iso.datetime().nullable(),
  /** owner / super admin only (null for other viewers) */
  phone: z.string().nullable(),
  email: z.string().nullable(),
  hasAccount: z.boolean(),
  tournamentCount: z.number(),
  createdAt: z.iso.datetime(),
});
export type PlayersProfile = z.infer<typeof PlayersProfileSchema>;

export const PlayersResultLineSchema = z.object({
  view: z.string(),
  position: z.number().nullable(),
  positionLabel: z.string(),
  rank: z.number(),
  gross: z.number().nullable(),
  net: z.number().nullable(),
  points: z.number().nullable(),
  toPar: z.number().nullable(),
  holesPlayed: z.number(),
  putts: z.number().nullable(),
  /**
   * finalized tournaments: how a tie on score was decided for this player (from the FINAL
   * snapshot row) — e.g. MANUAL = playoff ("플레이오프 우승 · 정규 36홀 동타"); absent when not tied
   */
  tiebreak: LeaderboardTiebreakSchema.optional(),
});

export const PlayersHistoryItemSchema = z.object({
  tournamentId: z.uuid(),
  tournamentName: z.string(),
  tenantId: z.uuid(),
  tenantName: z.string(),
  kind: TournamentKind,
  status: TournamentStatus,
  startDate: DateOnly,
  endDate: DateOnly,
  participantId: z.uuid(),
  bibNumber: z.number().nullable(),
  playStatus: PlayStatus,
  finalized: z.boolean(),
  results: z.array(PlayersResultLineSchema),
});
export const PlayersHistorySchema = z.object({ items: z.array(PlayersHistoryItemSchema) });

export const PlayersStatsSchema = z.object({
  profileId: z.uuid().nullable(),
  tournaments: z.number(),
  rounds: z.number(),
  holesPlayed: z.number(),
  averageScore18: z.number().nullable(),
  averagePutts18: z.number().nullable(),
  albatrosses: z.number(),
  eagles: z.number(),
  birdies: z.number(),
  pars: z.number(),
  bogeys: z.number(),
  doubleBogeysOrWorse: z.number(),
  holeInOnes: z.number(),
  par3Average: z.number().nullable(),
  par4Average: z.number().nullable(),
  par5Average: z.number().nullable(),
  bestRound: z.number().nullable(),
});
export type PlayersStats = z.infer<typeof PlayersStatsSchema>;

export const PlayersClaimRequestSchema = z.object({
  participantAccessToken: z.string().min(16).max(64),
});
export const PlayersClaimResponseSchema = z.object({
  profileId: z.uuid(),
  participantId: z.uuid(),
  tournamentId: z.uuid(),
  /** false when the participant was already linked to this profile */
  linked: z.boolean(),
});

export const PlayersTenantSearchQuerySchema = z.object({
  q: z.string().max(100).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export const PlayersTenantSearchItemSchema = z.object({
  profileId: z.uuid(),
  fullName: z.string(),
  gender: Gender.nullable(),
  birthYear: z.number().nullable(),
  nationality: z.string().nullable(),
  handicapIndex: z.number().nullable(),
  tournaments: z.number(),
  lastPlayedOn: DateOnly.nullable(),
});
export const PlayersTenantSearchSchema = page(PlayersTenantSearchItemSchema);

export const PlayersMergeRequestSchema = z.object({ otherProfileId: z.uuid() });
export const PlayersMergeResponseSchema = z.object({
  profileId: z.uuid(),
  mergedProfileId: z.uuid(),
  moved: z.object({
    participants: z.number(),
    participantResults: z.number(),
    handicapRecords: z.number(),
    handicapSnapshots: z.number(),
    clubMemberships: z.number(),
    seasonStandings: z.number(),
  }),
});

// =============================================================================================
// anomalies
// =============================================================================================

export const AnomalyFlagSchema = z.object({
  id: z.uuid(),
  tournamentId: z.uuid(),
  roundId: z.uuid().nullable(),
  roundNo: z.number().nullable(),
  kind: AnomalyKind,
  severity: AnomalySeverity,
  status: AnomalyStatus,
  caddy: z.object({ id: z.uuid(), code: z.string(), name: z.string() }).nullable(),
  participant: z
    .object({ id: z.uuid(), fullName: z.string(), bibNumber: z.number().nullable() })
    .nullable(),
  holeNumber: z.number().nullable(),
  dedupeKey: z.string(),
  details: z.record(z.string(), z.unknown()),
  note: z.string().nullable(),
  resolvedById: z.uuid().nullable(),
  resolvedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type AnomalyFlagDto = z.infer<typeof AnomalyFlagSchema>;

export const AnomaliesQuerySchema = z.object({
  status: AnomalyStatus.optional(),
  severity: AnomalySeverity.optional(),
  kind: AnomalyKind.optional(),
  roundId: z.uuid().optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});
export const AnomalyListSchema = page(AnomalyFlagSchema);

export const AnomalyUpdateRequestSchema = z
  .object({
    status: AnomalyStatus.optional(),
    note: z.string().max(1000).nullable().optional(),
  })
  .refine((v) => v.status !== undefined || v.note !== undefined, {
    message: 'status or note required',
  });

export const AnomaliesSummarySchema = z.object({
  tournamentId: z.uuid(),
  /**
   * tournament totals of score writes (additive): entries = first entries (CREATE), edits =
   * UPDATE + CLEAR + ADMIN_CORRECTION, rejected = refused score and contest writes, offline =
   * writes sent from the offline queue
   */
  totals: z
    .object({ entries: z.number(), edits: z.number(), rejected: z.number(), offline: z.number() })
    .optional(),
  open: z.object({
    total: z.number(),
    bySeverity: z.record(z.string(), z.number()),
    byKind: z.record(z.string(), z.number()),
  }),
  byCaddy: z.array(
    z.object({
      caddyId: z.uuid(),
      code: z.string(),
      name: z.string(),
      entries: z.number(),
      edits: z.number(),
      clears: z.number(),
      rejected: z.number(),
      offline: z.number(),
      /** edits / entries */
      editRate: z.number(),
      openFlags: z.number(),
    }),
  ),
  byParticipant: z.array(
    z.object({
      participantId: z.uuid(),
      fullName: z.string(),
      bibNumber: z.number().nullable(),
      edits: z.number(),
      adminCorrections: z.number(),
      openFlags: z.number(),
    }),
  ),
  rejectedByCode: z.array(z.object({ code: z.string(), count: z.number() })),
  offlineReplays: z.number(),
  /** score writes per local hour (tournament timezone), "yyyy-MM-dd HH:00" */
  hourly: z.array(z.object({ hour: z.string(), count: z.number() })),
});
export type AnomaliesSummary = z.infer<typeof AnomaliesSummarySchema>;

export const AnomaliesScanResponseSchema = z.object({
  tournamentId: z.uuid(),
  detected: z.number(),
  created: z.number(),
  updated: z.number(),
  byKind: z.record(z.string(), z.number()),
});
